/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, Fragment } from "react";

// antd:
// import { LoadingOutlined } from "@ant-design/icons";
import { Flex } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// Redux-hooks:
import { useAppSelector } from "../../libs/redux-hooks.ts"; // useAppDispatch
// Reducers:
import { getElementState, getLng, getUser, getTheme } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

const { rz } = window;

const RZflex:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZflex.displayName = "Flex";
  const user = useAppSelector(getUser);
  const theme = useAppSelector(getTheme);
  const lng = useAppSelector(getLng);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const elState = useAppSelector(getElementState(rootAttributes.key, fullPath)); // value, visible, disabled, loading, userStyle, userClass
  // XML задает стартовые props, а обработчики меняют elState.attributes через Redux.
  // Раньше Flex читал align/gap/wrap только из XML, поэтому динамические демо не меняли раскладку.
  const flexAttributes = {...attributes, ...elState.attributes};
  const {align, gap, wrap, justify, vertical, orientation, flex} = flexAttributes;
  const {borders, color, backgroundColor} = elState.attributes;

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    if (borders && !rz.arrContains(userClss, /^rz-borders-/igm)) {
      borders.split(" ").filter((ff) => ((/^(light|dark|l1|l2|l3|l4|l5|d1|d2|d3|d4|d5|hover)$/igm).test(ff)))
        .forEach((ff:string) => { if (!rz.arrContains(userClss, "rz-borders-" + ff)) { userClss.push("rz-borders-" + ff) } });
    }
    return userClss;
  }, [elState?.userClass, elState?.disabled, borders, permissions]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor) {
      if (bclr) { est.backgroundColor = bclr }
      else { est.backgroundColor = (theme === "light" ? "#fff" : "#141414") }
    }
    return est;
  }, [backgroundColor, color, elState.userStyle, elState?.visible, permissions.visible, theme]);

  const algn = useMemo<{row:string, column:string}>(() => {
    const aln = {row: "flex-start", column: "flex-start"};
    // Ant Design использует отдельные props justify и align, а старые XML-примеры
    // могли писать оба направления в одном align: например "spaceBetween middle".
    // Нормализуем оба формата, чтобы работали и новые AntD-подобные демо, и старые разметки.
    const getTokens = (value:string | number | boolean | undefined | null) => String(value || "").toLowerCase().split(/[\s|_-]+/igm).filter(Boolean);
    const normalizeRow = (value:string | number | boolean | undefined | null, fallback = "flex-start") => {
      const tokens = getTokens(value);
      const compact = tokens.join("");
      if (compact.includes("spacebetween")) { return "space-between" }
      if (compact.includes("spacearound")) { return "space-around" }
      if (compact.includes("spaceevenly")) { return "space-evenly" }
      if (tokens.includes("center")) { return "center" }
      if (tokens.includes("right") || tokens.includes("end") || compact.includes("flexend")) { return "flex-end" }
      if (tokens.includes("left") || tokens.includes("start") || compact.includes("flexstart")) { return "flex-start" }
      return fallback;
    };
    const normalizeColumn = (value:string | number | boolean | undefined | null, fallback = "flex-start") => {
      const tokens = getTokens(value);
      const compact = tokens.join("");
      if (tokens.includes("stretch")) { return "stretch" }
      if (tokens.includes("center") || tokens.includes("middle")) { return "center" }
      if (tokens.includes("bottom") || tokens.includes("end") || compact.includes("flexend")) { return "flex-end" }
      if (tokens.includes("top") || tokens.includes("start") || compact.includes("flexstart")) { return "flex-start" }
      return fallback;
    };
    if (justify) {
      aln.row = normalizeRow(justify);
      aln.column = normalizeColumn(align);
    }
    else {
      aln.row = normalizeRow(align);
      aln.column = normalizeColumn(align);
    }
    return aln;
  }, [align, justify]);

  // vertical появился как прямой prop Ant Design Flex; orientation оставлен как читаемый alias.
  const isVertical = useMemo<boolean>(() => (
    vertical === true || vertical === "true" || String(orientation || "").toLowerCase() === "vertical"
  ), [orientation, vertical]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);

  return (
    <Flex
      key={key} id={key} className={clsu.join(" ")} style={styl} uk-tooltip={tooltip}
      justify={algn.row}
      align={algn.column}
      gap={gap ? (rz.canbeNumber(gap) ? Number(gap) : gap) : "small"} // "small" || "middle" || "large"
      wrap={wrap === "true" ? "wrap" : (wrap === "false" ? "nowrap" : (wrap))} // "wrap" || "nowrap" || "wrap-reverse"
      vertical={isVertical}
      flex={flex}
    >
      {children}
    </Flex>
  );
}

export default RZflex;
/* eslint-enable @typescript-eslint/no-unused-vars */
